/**
 * What the Адреси page says about a place and a change, shared by its parts.
 */

import type { PlaceSnapshot, PlaceView } from '../../places/api';
import type { MapPin } from '../../places/PlacesMap';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Who made a change: the kind of person, and their name when the firm has it. */
export function who(
  t: Translate,
  source: string,
  userId: string | null | undefined,
  people: Readonly<Record<string, string>>,
): string {
  const name = userId == null ? undefined : people[userId];

  return name === undefined ? t(`places.source.${source}`) : `${t(`places.source.${source}`)} · ${name}`;
}

/** Metres between two points, near enough for "moved 8 m". */
export function metresBetween(
  a: { longitude: number; latitude: number },
  b: { longitude: number; latitude: number },
): number {
  const radians = Math.PI / 180;
  const dLat = (b.latitude - a.latitude) * radians;
  const dLon = (b.longitude - a.longitude) * radians;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.latitude * radians) * Math.cos(b.latitude * radians) * Math.sin(dLon / 2) ** 2;

  return 6_371_000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** What a change did, in one line: moved, renamed, its note or names or stop changed. */
export function whatChanged(t: Translate, before: PlaceSnapshot | null | undefined, after: PlaceSnapshot | null | undefined): string {
  if (after == null) return t('places.change.nothing');
  if (before == null) return t('places.change.placed');

  const parts: string[] = [];
  const moved = Math.round(metresBetween(before.door, after.door));

  if (moved > 0) parts.push(t('places.change.moved', { metres: moved }));
  if (before.building !== after.building || (before.entrance ?? '') !== (after.entrance ?? '')) {
    parts.push(t('places.change.renamed', { line: after.line }));
  }
  if ((before.note ?? '') !== (after.note ?? '')) parts.push(t('places.change.note'));
  if (before.names.join('|') !== after.names.join('|')) parts.push(t('places.change.names'));
  if (JSON.stringify(before.stop ?? null) !== JSON.stringify(after.stop ?? null)) parts.push(t('places.change.stop'));

  return parts.length === 0 ? t('places.change.nothing') : parts.join(' · ');
}

/** A place as a door on the map. */
export function pinOf(place: PlaceView): MapPin {
  return {
    id: place.id,
    longitude: place.door.longitude,
    latitude: place.door.latitude,
    label: place.entrance ?? '•',
    title: place.line,
    tone: place.reported ? 'reported' : place.state === 'waiting' ? 'waiting' : 'approved',
  };
}

/** The next letter of the Bulgarian alphabet, for placing a block's doors in order. */
export function nextEntrance(entrance: string): string {
  const letters = 'АБВГДЕЖЗИКЛМНОПРСТУФХЦЧШЩЮЯ';
  const at = letters.indexOf(entrance);

  if (at >= 0 && at < letters.length - 1) return letters[at + 1];
  if (/^\d+$/.test(entrance)) return String(Number(entrance) + 1);

  return '';
}
